// 오른쪽 패널 - 인건비 //

import React, { useState, useRef, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";

import api from "../../../api";
import ConfirmPopup from "../../../components/ConfirmPopup";
import styles from "./SalaryPanel.module.css";

// mode: null(일반 진입) | "labor"(인건비 빠른 입력) | "daily"(일용직 인건비 빠른 입력)
const SalaryPanel = forwardRef(function SalaryPanel(
  { onItemsChange, vendors = [], mode = null, draftDate = "", prefillServiceName = null },
  ref
) {

  const buildEmptyForm = () => ({
    name: "",
    serviceName: prefillServiceName?.name || "",
    serviceNameId: prefillServiceName?.id || null,
    totalSalary: "",
    incomeTax: mode === "daily" ? "0" : "",
    localTax: mode === "daily" ? "0" : "",
    health: mode === "daily" ? "0" : "",
    care: mode === "daily" ? "0" : "",
    pension: mode === "daily" ? "0" : "",
    employment: mode === "daily" ? "0" : "",
  });

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(buildEmptyForm);

  // ✅ 빠른 입력 흐름에서 선택된 용역명을 입력행에 자동 채움 — 이미 입력을
  // 시작한(이름/급여합계 입력됨) 행은 덮어쓰지 않는다.
  useEffect(() => {
    if (!prefillServiceName?.name) return;
    setForm((f) => (
      !f.name && !f.totalSalary
        ? { ...f, serviceName: prefillServiceName.name, serviceNameId: prefillServiceName.id }
        : f
    ));
  }, [prefillServiceName]);

  // ✅ 용역명 [수정] 후 재선택 시 — 이미 추가된 명단 행들의 용역명 컬럼도 갱신.
  // (최초 마운트 때는 items가 비어있어 실질적으로 아무 일도 안 일어남)
  const prevPrefillRef = useRef(null);
  useEffect(() => {
    const prev = prevPrefillRef.current;
    prevPrefillRef.current = prefillServiceName;
    if (!prefillServiceName?.name) return;
    if (prev && prev.name === prefillServiceName.name && prev.id === prefillServiceName.id) return;
    setItems((old) => {
      if (old.length === 0) return old;
      const updated = old.map((it) => ({
        ...it, serviceName: prefillServiceName.name, serviceNameId: prefillServiceName.id,
      }));
      onItemsChange?.(updated);
      return updated;
    });
  }, [prefillServiceName, onItemsChange]);

  // ✅ 인건비(급여합계 기준) 자동계산 팝업 상태
  const [pendingTaxCalc, setPendingTaxCalc] = useState(false);
  const [calculatingTax, setCalculatingTax] = useState(false);
  const [appliedRateYear, setAppliedRateYear] = useState(null);
  const incomeTaxInputRef = useRef(null);

  // ── 단축키용 ref ──
  const firstInputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    focusFirst: () => firstInputRef.current?.focus(),
  }));

  // 용역명 드롭다운
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendorHighlight, setVendorHighlight] = useState(-1);
  const vendorListRef  = useRef(null);
  const vendorInputRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 220 });

  const comma   = (n) => (n ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "");
  const uncomma = (n) => String(n).replace(/,/g, "");

  const calcTotal = (ts, it, lt, h, c, p, e) => ts - it - lt - h - c - p - e;

  // 용역명 필터
  const filteredVendors = useMemo(
    () => vendors.filter((v) => v.name.toLowerCase().includes(form.serviceName.toLowerCase())),
    [vendors, form.serviceName]
  );

  // 드롭다운 위치 계산 (fixed)
  const openVendorList = () => {
    if (vendorInputRef.current) {
      const rect = vendorInputRef.current.getBoundingClientRect();
      setDropdownPos({
        top:   rect.bottom + 2,
        left:  rect.left,
        width: Math.max(rect.width, 220),
      });
    }
    setShowVendorList(true);
    setVendorHighlight(-1);
  };

  // 스크롤 시 드롭다운 위치 갱신
  useEffect(() => {
    if (!showVendorList) return;
    const update = () => {
      if (vendorInputRef.current) {
        const rect = vendorInputRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 220) });
      }
    };
    window.addEventListener("scroll", update, true);
    return () => window.removeEventListener("scroll", update, true);
  }, [showVendorList]);

  const scrollVendor = (idx) => {
    if (!vendorListRef.current) return;
    const els = vendorListRef.current.querySelectorAll("[data-item]");
    if (els[idx]) els[idx].scrollIntoView({ block: "nearest" });
  };

  const handleVendorKeyDown = (e) => {
    if (!showVendorList) {
      if (e.key === "ArrowDown") { openVendorList(); setVendorHighlight(0); }
      return;
    }
    if (e.key === "Escape") { setShowVendorList(false); setVendorHighlight(-1); }
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      setVendorHighlight((p) => { const n = Math.min(p + 1, filteredVendors.length - 1); scrollVendor(n); return n; });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setVendorHighlight((p) => { const n = Math.max(p - 1, 0); scrollVendor(n); return n; });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const v = filteredVendors[vendorHighlight];
      if (v) {
        setForm((f) => ({ ...f, serviceName: v.name, serviceNameId: v.id }));
        setShowVendorList(false); setVendorHighlight(-1);
      }
    } else if (e.key === "Tab") {
      setShowVendorList(false);
    }
  };

  const selectVendor = (v) => {
    setForm((f) => ({ ...f, serviceName: v.name, serviceNameId: v.id }));
    setShowVendorList(false); setVendorHighlight(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numFields = ["totalSalary","incomeTax","localTax","health","care","pension","employment"];
    setForm((f) => ({
      ...f,
      [name]: numFields.includes(name) ? comma(value.replace(/,/g, "")) : value,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name === "employment") {
      e.preventDefault();
      addItem();
    }
  };

  // ✅ 급여합계 입력 후 Tab/Enter → "소득세/지방소득세 자동 등록?" 팝업
  // (인건비 빠른 입력 흐름에서만 — 일반 진입/일용직은 손대지 않음)
  const handleTotalSalaryKeyDown = (e) => {
    if (mode !== "labor") return;
    if (e.key !== "Tab" && e.key !== "Enter") return;
    const ts = parseInt(uncomma(form.totalSalary));
    if (!ts) return;
    e.preventDefault();
    setPendingTaxCalc(true);
  };

  const confirmTaxCalc = async () => {
    setPendingTaxCalc(false);
    const ts = parseInt(uncomma(form.totalSalary)) || 0;
    const year = Number((draftDate || "").slice(0, 4)) || new Date().getFullYear();
    setCalculatingTax(true);
    try {
      const [taxRes, insRes] = await Promise.all([
        api.get("payroll/tax-table/lookup/", { params: { salary: ts, date: draftDate } }),
        api.get("insurance/rates/for-year/", { params: { year } }),
      ]);
      const { income_tax, local_tax } = taxRes.data;
      const r = insRes.data;
      const health = Math.round((ts * r.health_rate) / 100);
      const care = Math.round((health * r.care_rate) / 100);
      const pension = Math.round((ts * r.pension_rate) / 100);
      const employment = Math.round((ts * r.employ_rate) / 100);

      setForm((f) => ({
        ...f,
        incomeTax: comma(String(income_tax)),
        localTax: comma(String(local_tax)),
        health: comma(String(health)),
        care: comma(String(care)),
        pension: comma(String(pension)),
        employment: comma(String(employment)),
      }));
      setAppliedRateYear(r.matched_year);
    } catch (err) {
      alert("자동 계산 실패: " + (err?.response?.data?.detail || err.message));
    } finally {
      setCalculatingTax(false);
      incomeTaxInputRef.current?.focus();
    }
  };

  const cancelTaxCalc = () => {
    setPendingTaxCalc(false);
    incomeTaxInputRef.current?.focus();
  };

  const addItem = () => {
    if (!form.name.trim()) return alert("이름을 입력하세요");
    const ts = parseInt(uncomma(form.totalSalary));
    if (isNaN(ts)) return alert("급여 합계를 입력하세요");

    // ✅ 일용직 인건비 빠른 입력: 소득세/지방소득세/4대보험 전부 0 고정
    const isDaily = mode === "daily";
    const it = isDaily ? 0 : parseInt(uncomma(form.incomeTax))  || 0;
    const lt = isDaily ? 0 : parseInt(uncomma(form.localTax))   || 0;
    const h  = isDaily ? 0 : parseInt(uncomma(form.health))     || 0;
    const c  = isDaily ? 0 : parseInt(uncomma(form.care))       || 0;
    const p  = isDaily ? 0 : parseInt(uncomma(form.pension))    || 0;
    const em = isDaily ? 0 : parseInt(uncomma(form.employment)) || 0;

    const newItem = {
      name:          form.name,
      serviceName:   form.serviceName,
      serviceNameId: form.serviceNameId,
      totalSalary:   ts,
      incomeTax:     it,
      localTax:      lt,
      health:        h,
      care:          c,
      pension:       p,
      employment:    em,
      total:         calcTotal(ts, it, lt, h, c, p, em),
    };

    const updated = [...items, newItem];
    setItems(updated);
    onItemsChange?.(updated);
    setForm(buildEmptyForm());
  };

  const removeItem = (idx) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    const updated = items.filter((_, i) => i !== idx);
    setItems(updated);
    onItemsChange?.(updated);
  };

  const vendorInvalid =
    form.serviceName.trim().length > 0 &&
    !vendors.some((v) => v.name.trim() === form.serviceName.trim());

  const sum = (key) => items.reduce((a, b) => a + (b[key] || 0), 0);

  const previewTotal = form.totalSalary
    ? calcTotal(
        parseInt(uncomma(form.totalSalary)) || 0,
        parseInt(uncomma(form.incomeTax))   || 0,
        parseInt(uncomma(form.localTax))    || 0,
        parseInt(uncomma(form.health))      || 0,
        parseInt(uncomma(form.care))        || 0,
        parseInt(uncomma(form.pension))     || 0,
        parseInt(uncomma(form.employment))  || 0,
      )
    : 0;

  return (
    <div className={styles.sidePane}>
      <div className={styles.scrollWrap}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th style={{ width: 120, whiteSpace: "nowrap" }}>이름</th>
              <th style={{ width: 220, whiteSpace: "nowrap" }}>용역명</th>
              <th style={{ width: 120, minWidth: 120, whiteSpace: "nowrap" }}>급여 합계</th>
              <th style={{ width: 100, whiteSpace: "nowrap" }}>소득세</th>
              <th style={{ width: 100, whiteSpace: "nowrap" }}>지방소득세</th>
              <th style={{ width: 100, whiteSpace: "nowrap" }}>건강보험</th>
              <th style={{ width: 100, whiteSpace: "nowrap" }}>요양보험</th>
              <th style={{ width: 100, whiteSpace: "nowrap" }}>국민연금</th>
              <th style={{ width: 100, whiteSpace: "nowrap" }}>고용보험</th>
              <th style={{ width: 200, whiteSpace: "nowrap" }}>실지급액(자동)</th>
              <th style={{ whiteSpace: "nowrap" }}>추가</th>
            </tr>
          </thead>
          <tbody>
            {/* ── 입력행 ── */}
            <tr className={styles.inputRow}>
              <td>
                <input
                    ref={firstInputRef}
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="이름"
                    style={{ width: 130, textAlign: "center" }}
                  />
                  

              </td>

              {/* 용역명 자동완성 */}
              <td style={{ minWidth: 220 }}>
                <div style={{ position: "relative" }}>
                  <input
                    ref={vendorInputRef}
                    name="serviceName"
                    value={form.serviceName}
                    placeholder="용역명 검색"
                    onChange={(e) => {
                      setForm((f) => ({ ...f, serviceName: e.target.value, serviceNameId: null }));
                      openVendorList();
                    }}
                    onFocus={openVendorList}
                    onBlur={() => setTimeout(() => setShowVendorList(false), 150)}
                    onKeyDown={handleVendorKeyDown}
                    style={{
                      width: "100%",
                      ...(vendorInvalid
                        ? { borderColor: "#ef4444", background: "#fff5f5" }
                        : form.serviceNameId
                        ? { borderColor: "#10b981", background: "#f0fdf4" }
                        : {}),
                    }}
                  />
                  <button
                    type="button"
                    style={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)", fontSize: 10, padding: "2px 4px" }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => showVendorList ? setShowVendorList(false) : openVendorList()}
                  >▼</button>
                </div>
                {vendorInvalid && (
                  <div style={{ color: "#ef4444", fontSize: 11, marginTop: 2 }}>⚠ 미등록 거래처</div>
                )}
              </td>

              <td>
                <input
                  name="totalSalary" value={form.totalSalary} onChange={handleChange}
                  onKeyDown={handleTotalSalaryKeyDown} disabled={calculatingTax}
                />
                {mode === "labor" && appliedRateYear && (
                  <div style={{ fontSize: 10, color: "#2563eb", marginTop: 2, whiteSpace: "nowrap" }}>
                    {appliedRateYear}년 요율 적용
                  </div>
                )}
              </td>
              <td><input ref={incomeTaxInputRef} name="incomeTax" value={form.incomeTax} onChange={handleChange} disabled={mode === "daily"} /></td>
              <td><input name="localTax"     value={form.localTax}     onChange={handleChange} disabled={mode === "daily"} /></td>
              <td><input name="health"       value={form.health}       onChange={handleChange} disabled={mode === "daily"} /></td>
              <td><input name="care"         value={form.care}         onChange={handleChange} disabled={mode === "daily"} /></td>
              <td><input name="pension"      value={form.pension}      onChange={handleChange} disabled={mode === "daily"} /></td>
              <td><input name="employment"   value={form.employment}   onChange={handleChange} onKeyDown={handleKeyDown} disabled={mode === "daily"} /></td>
              <td className={styles.calcCell}>{comma(previewTotal)}</td>
              <td><button onClick={addItem}>추가</button></td>
            </tr>

            {/* ── 데이터 행 ── */}
            {items.map((item, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: "center" }}>{item.name}</td>

                <td>{item.serviceName || "-"}</td>
                <td>{comma(item.totalSalary)}</td>
                <td>{comma(item.incomeTax)}</td>
                <td>{comma(item.localTax)}</td>
                <td>{comma(item.health)}</td>
                <td>{comma(item.care)}</td>
                <td>{comma(item.pension)}</td>
                <td>{comma(item.employment)}</td>
                <td>{comma(item.total)}</td>
                <td><button onClick={() => removeItem(idx)}>삭제</button></td>
              </tr>
            ))}

            {/* ── 합계 행 ── */}
            <tr className={styles.sumRow}>
              <td>합계</td>
              <td></td>
              <td>{comma(sum("totalSalary"))}</td>
              <td>{comma(sum("incomeTax"))}</td>
              <td>{comma(sum("localTax"))}</td>
              <td>{comma(sum("health"))}</td>
              <td>{comma(sum("care"))}</td>
              <td>{comma(sum("pension"))}</td>
              <td>{comma(sum("employment"))}</td>
              <td>{comma(sum("total"))}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── 드롭다운: position fixed → overflow 클리핑 완전 무관 ── */}
      {showVendorList && (
        <div
          ref={vendorListRef}
          style={{
            position: "fixed",
            top:      dropdownPos.top,
            left:     dropdownPos.left,
            width:    dropdownPos.width,
            zIndex:   9999,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            maxHeight: 320,
            overflowY: "auto",
            boxShadow: "0 6px 16px rgba(0,0,0,.15)",
          }}
        >
          <div style={{
            padding: "4px 10px", fontSize: 11, color: "#6b7280",
            borderBottom: "1px solid #e5e7eb", background: "#f9fafb",
            position: "sticky", top: 0,
          }}>
            {filteredVendors.length}건
          </div>
          {filteredVendors.length > 0
            ? filteredVendors.map((v, i) => (
                <div
                  key={v.id}
                  data-item
                  onMouseEnter={() => setVendorHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectVendor(v)}
                  style={{
                    padding: "7px 10px", cursor: "pointer", fontSize: 13,
                    background: i === vendorHighlight ? "#eff6ff" : "transparent",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  {v.name}
                </div>
              ))
            : (
              <div style={{ padding: "8px 10px", color: "#9ca3af", fontSize: 13 }}>
                일치하는 거래처 없음
              </div>
            )}
        </div>
      )}

      {pendingTaxCalc && (
        <ConfirmPopup
          message={"소득세, 지방소득세를 자동으로 등록하시겠습니까?"}
          onConfirm={confirmTaxCalc}
          onCancel={cancelTaxCalc}
        />
      )}
    </div>
  );
});

export default SalaryPanel;